/** @jsxImportSource @emotion/react */
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Autocomplete, {
  AutocompleteCloseReason,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase'
import OutlinedInput from '@mui/material/OutlinedInput'
import {
  ReactNode,
  useMemo,
  useState,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useCallback,
  SyntheticEvent,
  HTMLAttributes,
} from 'react'
import { uniqBy } from 'lodash'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'
import { AutocompleteInputChangeReason } from '@mui/material'
import { Interpolation } from '@emotion/serialize'
import { Theme } from '@emotion/react'
import * as classes from './styles'
import { ReactComponent as CheckIcon } from './icons/check.svg'
import { ReactComponent as SearchIcon } from './icons/search.svg'
import { ReactComponent as ChevronDownIcon } from './icons/chevron-down.svg'

export type BaseItem = { id: string; title?: string }

interface PopperComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

function PopperComponent(props: PopperComponentProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal, anchorEl, open, ...other } = props
  return <div {...other} css={classes.autocompletePopper} />
}

function renderItemDefault<Item extends BaseItem>(item: Item) {
  return item.title ?? ''
}

function getItemLabelDefault<Item extends BaseItem>(item: Item) {
  return item.title ?? ''
}

function isItemEqualToValueDefault<Item extends BaseItem>(item: Item, value: Item) {
  return item.id === value.id
}

export type AutosuggestItemActions = {
  close: () => void
}

export type AutosuggestProps<Item> = Omit<ButtonBaseProps, 'value' | 'onChange' | 'type'> & {
  items: Item[]
  value: Item[]
  onChange: (value: Item[]) => void
  onInputChange?: (searchValue: string) => void
  renderItem?: (item: Item, state: AutocompleteRenderOptionState, actions: AutosuggestItemActions) => ReactNode
  getItemLabel?: (item: Item) => string
  isItemEqualToValue?: (item: Item, value: Item) => boolean
  loading?: boolean
  disabled?: boolean
  error?: boolean
  multiple?: boolean
  searchable?: boolean
  changeImmediately?: boolean
  displaySelectedItemsFirst?: boolean
  hideSelectedItems?: boolean
  maxSelectedItemsCount?: number
  label?: string
  formHelperText?: string
  placeholder?: string
  searchPlaceholder?: string
  searchLoadingText?: string
  searchNothingFoundText?: string
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  withoutCheckMark?: boolean
  className?: string
  popperCss?: Interpolation<Theme>
  disabledItemIds?: string[]
}

export function Autosuggest<Item extends BaseItem>({
  items,
  value,
  onChange,
  onInputChange,
  onClick,
  renderItem = renderItemDefault,
  getItemLabel = getItemLabelDefault,
  isItemEqualToValue = isItemEqualToValueDefault,
  loading,
  disabled,
  error,
  multiple,
  searchable,
  changeImmediately,
  displaySelectedItemsFirst,
  hideSelectedItems,
  maxSelectedItemsCount,
  label,
  formHelperText,
  placeholder = '',
  searchPlaceholder = 'Search',
  searchLoadingText = 'Loading...',
  searchNothingFoundText = 'Nothing found',
  size = 'large',
  fullWidth,
  withoutCheckMark = false,
  className,
  popperCss,
  disabledItemIds = [],
  ...props
}: AutosuggestProps<Item>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [pendingValue, setPendingValue] = useState<Item[]>([...value])
  const [isChanged, setIsChanged] = useState(false)
  const [popperWidth, setPopperWidth] = useState<number | undefined>()

  const selectedItemsCount = pendingValue.length
  const isMaxSelectedItemsCountReached =
    maxSelectedItemsCount !== undefined && selectedItemsCount >= maxSelectedItemsCount

  const options = useMemo(() => {
    if (hideSelectedItems) {
      const selectedItemIds = value.map(({ id }) => id)
      return items.filter(({ id }) => !selectedItemIds.includes(id))
    }
    if (!displaySelectedItemsFirst) {
      return [...items]
    }
    return uniqBy([...value, ...items], 'id')
  }, [hideSelectedItems, displaySelectedItemsFirst, value, items])

  useEffect(() => {
    setPendingValue([...value])
  }, [value])

  const dismissPopper = useCallback(() => {
    setAnchorEl(null)
  }, [anchorEl])

  useEffect(() => {
    if (!anchorEl) {
      return
    }

    function handleResize(): void {
      setPopperWidth(anchorEl?.getBoundingClientRect().width)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [anchorEl])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setIsChanged(false)
      setAnchorEl(event.currentTarget)
      onClick?.(event)
    },
    [onClick]
  )

  const handleClose = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()

      isChanged && onChange(pendingValue)
      if (anchorEl) {
        anchorEl.focus()
      }
      setAnchorEl(null)
    },
    [isChanged, onChange, pendingValue, anchorEl]
  )

  const handleAutocompleteChange = useCallback(
    (event: SyntheticEvent, newValue: Item | Item[] | null, reason: AutocompleteChangeReason) => {
      if (event.type === 'keydown' && (event as KeyboardEvent).key === 'Backspace' && reason === 'removeOption') {
        return
      }

      if (!multiple) {
        const newPendingValue = Array.isArray(newValue)
          ? !newValue.length
            ? []
            : [newValue[newValue.length - 1]]
          : !newValue
          ? []
          : [newValue]
        setPendingValue(newPendingValue)
        onChange(newPendingValue)
        if (anchorEl) {
          anchorEl.focus()
        }
        setAnchorEl(null)
        return
      }

      setIsChanged(true)
      let newPendingValue = !newValue ? [] : Array.isArray(newValue) ? newValue : [newValue]

      if (maxSelectedItemsCount !== undefined && newPendingValue.length > maxSelectedItemsCount) {
        newPendingValue = newPendingValue.slice(0, maxSelectedItemsCount)
      }

      if (changeImmediately) {
        onChange(newPendingValue)
      } else {
        setPendingValue(newPendingValue)
      }
    },
    [multiple, anchorEl, onChange, changeImmediately, maxSelectedItemsCount]
  )

  const handleAutocompleteClose = useCallback(
    (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
      if (reason === 'escape') {
        handleClose(event)
      }
    },
    [handleClose]
  )

  const handleAutocompleteInputChange = useCallback(
    (event: SyntheticEvent, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      if (event?.type !== 'blur' && ['reset', 'selectOption', 'removeOption'].includes(reason)) {
        return
      }

      setInputValue(newInputValue)
      onInputChange?.(newInputValue)
    },
    [handleClose]
  )

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, item: Item, state: AutocompleteRenderOptionState) => (
      <li {...props} key={item.id}>
        {renderItem(item, state, { close: dismissPopper })}
        {!withoutCheckMark && state.selected && <CheckIcon css={classes.checkIcon} />}
      </li>
    ),
    [renderItem, dismissPopper, withoutCheckMark]
  )

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <OutlinedInput
        ref={params.slotProps.input.ref}
        inputProps={params.slotProps.htmlInput}
        placeholder={searchPlaceholder}
        startAdornment={<SearchIcon css={classes.searchIcon} />}
        endAdornment={
          loading && (
            <div css={classes.loader}>
              <CircularProgress size={22} />
            </div>
          )
        }
        css={classes.searchInput(!searchable)}
        /* eslint-disable-next-line jsx-a11y/no-autofocus */
        autoFocus
      />
    ),
    [loading, searchable, searchPlaceholder]
  )

  const open = Boolean(anchorEl)

  const controlButton = (
    <ButtonBase
      onClick={handleClick}
      disabled={disabled}
      css={classes.baseButton({ open, error, size })}
      disableRipple
      data-testid="search-button"
      {...props}
    >
      {pendingValue.length ? (
        <span>{pendingValue.map(getItemLabel).join(', ')}</span>
      ) : (
        <span className="placeholder">{placeholder}</span>
      )}
      <ChevronDownIcon className="chevron" data-testid="expand-search-button" />
    </ButtonBase>
  )

  return (
    <div className={className} css={classes.wrapper(fullWidth)}>
      <div className="base">
        {label ? (
          <FormControlLabel control={controlButton} label={label} labelPlacement="top" css={classes.labelWrapper} />
        ) : (
          controlButton
        )}
        {!!formHelperText && <FormHelperText error={error}>{formHelperText}</FormHelperText>}
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        css={[classes.popper(popperWidth), popperCss]}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Autocomplete
              open
              multiple
              loading={loading}
              onClose={handleAutocompleteClose}
              value={pendingValue}
              onChange={handleAutocompleteChange}
              inputValue={inputValue}
              onInputChange={handleAutocompleteInputChange}
              disableCloseOnSelect
              clearOnEscape={true}
              renderValue={() => null}
              loadingText={searchLoadingText}
              noOptionsText={searchNothingFoundText}
              renderOption={renderOption}
              options={options}
              isOptionEqualToValue={isItemEqualToValue}
              getOptionLabel={getItemLabel}
              renderInput={renderInput}
              getOptionDisabled={(option) => {
                if (disabledItemIds.includes(option.id)) {
                  return true
                }
                // Disable unselected options when maxSelectedItemsCount is reached
                if (isMaxSelectedItemsCountReached && !pendingValue.find((item) => item.id === option.id)) {
                  return true
                }
                return false
              }}
              slots={{
                popper: PopperComponent,
              }}
            />
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  )
}
